import React, { ReactNode, useRef, useEffect, useState, memo } from "react";

interface ExpandableBoxProps {
  children: ReactNode;
  className?: string;
  containerStyles?: React.CSSProperties;
  innerContainerStyles?: React.CSSProperties;
}

// Container wrapper that handles smooth animation from one stage to the next stage within the same container
const ExpandableBox = ({
  children,
  className,
  containerStyles,
  innerContainerStyles,
}: ExpandableBoxProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  // Function to update the height
  const updateHeight = () => {
    if (containerRef.current) {
      setHeight(containerRef.current.offsetHeight);
    }
  };

  useEffect(() => {
    const currentContainerRef = containerRef.current;
    // Add a window resized event listener
    const observer = new ResizeObserver(() => {
      updateHeight();
    });

    // Add short timeout to stabilize the child element height
    setTimeout(() => {
      if (currentContainerRef && height !== currentContainerRef.offsetHeight) {
        setHeight(currentContainerRef.offsetHeight);
      }

      if (currentContainerRef) {
        observer.observe(currentContainerRef);
      }
    }, 100);

    return () => {
      // Cleanup the event listener on unmount
      if (currentContainerRef) {
        observer.unobserve(currentContainerRef);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <div
      className={className}
      style={{
        ...containerStyles,
        height: `${height}px`,
        minHeight: `${height}px`,
        maxHeight: `${height}px`,
        overflowX: "visible",
        overflowY: "clip",
        transition: "all 0.2s ease-in-out",
      }}
    >
      <div ref={containerRef} style={innerContainerStyles}>
        {children}
      </div>
    </div>
  );
};

export default memo(ExpandableBox);
